import React, { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { calculateOrderPrice, formatPriceWithCurrency } from "@helpers/utils";
import { isGroup, isLine, LineSchema, SaleOrdersFormTyping } from "../../types";
import { useAppSelector } from "@/redux/hooks";
import { SaleOrder } from "@/types/general";
import ContributionMarginDetails from "../../../ContributionMarginDetails";

export default function SummaryBlock() {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.orders" });
    const { control } = useFormContext<SaleOrdersFormTyping>();
    const baseCurrency = useAppSelector((state) => state.auth.user?.company?.currency);

    // Watch the entire "lines" array.
    const lines = useWatch({ control, name: "lines" });

    // Flatten the lines array: if an item is a group, push its contained lines.
    const flatLines = useMemo(() => {
        return (lines || []).reduce((acc, item) => {
            if (isLine(item)) {
                acc.push(item);
            } else if (isGroup(item)) {
                acc.push(...item.lines);
            }
            return acc;
        }, [] as LineSchema[]);
    }, [lines]);

    const linesSummary = useMemo(() => {
        let subtotal = 0;
        let taxes = 0;

        // Contribution margin (dekningsbidrag) accumulators. Mirrors the backend formula so the
        // panel matches the summary served for the same order once it is saved.
        let discountTotal = 0;
        let listPriceTotal = 0;
        let indirectCostTotal = 0;

        flatLines.forEach((line) => {
            const quantity = line.quantity;
            const unitPrice = line.unit_price;
            const discount = line.discount ?? 0;
            const taxRate = line.tax?.rate ?? 0;

            const linePrice = calculateOrderPrice(quantity, unitPrice, discount);
            subtotal += linePrice;
            taxes += linePrice * (taxRate / 100);

            discountTotal += unitPrice * quantity * (discount / 100);
            // Per the backend contract a missing cost contributes 0, making the line read as
            // 100% margin. That is "no list price entered", not an error to guard against.
            listPriceTotal += (line.product.prices.list_price ?? 0) * quantity;
            indirectCostTotal += (line.product.prices.extra_cost ?? 0) * quantity;
        });

        // subtotal is already VAT-exclusive and net of discount, which is exactly the backend's
        // net_revenue. Reusing it keeps the margin consistent with the Subtotal row above.
        const contributionMargin = subtotal - listPriceTotal - indirectCostTotal;

        const margin: SaleOrder.Components.ContributionMarginSummary = {
            net_revenue: subtotal,
            list_price_total: listPriceTotal,
            indirect_cost_total: indirectCostTotal,
            discount_total: discountTotal,
            contribution_margin: contributionMargin,
            contribution_margin_percentage:
                subtotal !== 0 ? (contributionMargin / subtotal) * 100 : 0,
            line_count: flatLines.length,
        };

        return {
            subtotal,
            taxes,
            totalWithTaxes: subtotal + taxes,
            margin,
        };
    }, [flatLines]);

    return (
        <>
            <div className="flex justify-end mt-[48px] font-thin">
                <div className="w-1/2 grid grid-cols-2 gap-y-[16px]">
                    <div className="text-right">{t("summary.subtotal")}</div>
                    <div className="text-right pr-[16px]">
                        {formatPriceWithCurrency(linesSummary.subtotal, baseCurrency)}
                    </div>
                    <div className="text-right">{t("summary.taxes")}</div>
                    <div className="text-right pr-[16px]">
                        {formatPriceWithCurrency(linesSummary.taxes, baseCurrency)}
                    </div>
                    <div className="text-right font-semibold text-accent">{t("summary.total")}</div>
                    <div className="text-right pr-[16px] font-semibold text-accent">
                        {formatPriceWithCurrency(linesSummary.totalWithTaxes, baseCurrency)}
                    </div>
                </div>
            </div>

            <ContributionMarginDetails summary={linesSummary.margin} />
        </>
    );
}
